<template>
  <div class="space-y-5">
    <div
      v-if="errorMessage"
      class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
    >
      {{ errorMessage }}
    </div>

    <div class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <div class="border-b border-zinc-200 px-5 py-4">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 class="text-base font-semibold text-zinc-900">
            Upcoming renewals
          </h3>
          <p class="mt-1 text-sm text-zinc-500">
            See which recurring member payments are expected in the selected window.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <PlatformaSelect
            v-model="renewalsWindowDays"
            :options="renewalWindowOptions"
            class="w-36"
          />
          <button
            type="button"
            class="h-11 rounded-md border border-zinc-300 bg-white px-3 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isLoadingRenewals"
            @click="loadUpcomingRenewals"
          >
            Refresh
          </button>
        </div>
      </div>
      </div>

      <div
        v-if="isLoadingRenewals"
        class="px-5 py-14 text-center text-sm text-zinc-500"
      >
        Loading upcoming renewals...
      </div>
      <div
        v-else-if="!upcomingRenewals.length"
        class="px-5 py-14 text-center"
      >
        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-[#2D8CFF]">
          <CalendarDaysIcon class="h-6 w-6" />
        </div>
        <p class="mt-3 text-sm font-semibold text-zinc-900">No renewals in this window</p>
        <p class="mx-auto mt-1 max-w-md text-sm text-zinc-500">Try a wider date range, or check back as recurring memberships become due.</p>
      </div>
      <div
        v-else
        class="app-scroll overflow-x-auto"
      >
        <table class="min-w-full text-left text-sm">
          <thead class="text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th class="px-2 py-2 font-semibold">Member</th>
              <th class="px-2 py-2 font-semibold">Tier</th>
              <th class="px-2 py-2 font-semibold">Amount</th>
              <th class="px-2 py-2 font-semibold">Next Billing</th>
              <th class="px-2 py-2 font-semibold">Due In</th>
              <th class="px-2 py-2 font-semibold">Billing Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in upcomingRenewals"
              :key="`upcoming-renewal-${item.memberUserId}-${item.planTierId}-${item.nextBillingAt}`"
              class="border-t border-zinc-200"
            >
              <td class="px-2 py-2">
                <p class="font-semibold text-zinc-900">{{ item.memberName }}</p>
                <p class="text-xs text-zinc-500">{{ item.memberEmail || `Member #${item.memberUserId}` }}</p>
              </td>
              <td class="px-2 py-2 text-zinc-800">{{ item.planTierName }}</td>
              <td class="px-2 py-2 text-zinc-800">{{ formatRenewalAmount(item) }}</td>
              <td class="px-2 py-2 text-zinc-800">{{ formatDateTime(item.nextBillingAt) }}</td>
              <td class="px-2 py-2 text-zinc-700">{{ formatDueIn(item.dueInDays) }}</td>
              <td class="px-2 py-2">
                <span class="inline-flex rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-semibold capitalize text-zinc-700">
                  {{ item.billingStatus || 'current' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import { CalendarDaysIcon } from '@heroicons/vue/24/outline';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const isLoadingRenewals = ref(false);
const renewalsWindowDays = ref('7');
const upcomingRenewals = ref([]);
const errorMessage = ref('');

const renewalWindowOptions = Object.freeze([
  { value: '7', label: 'Next 7 days' },
  { value: '30', label: 'Next 30 days' },
  { value: '90', label: 'Next 90 days' },
]);

const readJsonResponse = async (response, fallbackMessage = 'Request failed') => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(String(payload?.message || fallbackMessage));
  }
  return payload;
};

const formatAmount = (cents, currencyCode = 'USD') => {
  const normalizedCents = Number.parseInt(String(cents ?? 0), 10);
  const safeCents = Number.isFinite(normalizedCents) ? normalizedCents : 0;
  const amount = safeCents / 100;
  const code = String(currencyCode || 'USD').trim().toUpperCase() || 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (_error) {
    return `$${amount.toFixed(2)} ${code}`;
  }
};

const formatDateTime = (value) => {
  const date = value ? new Date(value) : null;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const formatDueIn = (days) => {
  const normalizedDays = Number.parseInt(String(days ?? 0), 10);
  if (!Number.isFinite(normalizedDays) || normalizedDays <= 0) return 'Today';
  if (normalizedDays === 1) return '1 day';
  return `${normalizedDays} days`;
};

const formatRenewalAmount = (item) =>
  formatAmount(item?.priceCents, item?.currencyCode || 'USD');

const loadUpcomingRenewals = async () => {
  isLoadingRenewals.value = true;
  errorMessage.value = '';
  try {
    const windowDays = String(renewalsWindowDays.value || '7').trim();
    const response = await fetch(
      `${apiProxyUrl}/api/creator-payments/creator/upcoming-renewals?windowDays=${encodeURIComponent(windowDays)}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    const payload = await readJsonResponse(response, 'Failed to load upcoming renewals');
    upcomingRenewals.value = Array.isArray(payload?.items) ? payload.items : [];
  } catch (error) {
    upcomingRenewals.value = [];
    errorMessage.value = String(error?.message || 'Failed to load upcoming renewals');
  } finally {
    isLoadingRenewals.value = false;
  }
};

watch(
  () => renewalsWindowDays.value,
  () => {
    void loadUpcomingRenewals();
  }
);

onMounted(() => {
  void loadUpcomingRenewals();
});
</script>

<style scoped>
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(113, 113, 122, 0.38) transparent;
}
.app-scroll::-webkit-scrollbar { height: 7px; }
.app-scroll::-webkit-scrollbar-track { background: transparent; }
.app-scroll::-webkit-scrollbar-thumb { border-radius: 999px; background: rgba(113, 113, 122, 0.34); }
.app-scroll::-webkit-scrollbar-thumb:hover { background: rgba(82, 82, 91, 0.5); }
</style>
